import { useEffect, useState } from 'react';
import {
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Button } from '../../components/Button';
import { useTranslation } from '../../hooks/useTranslation';

const inputSx = {
  backgroundColor: '#fff',
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: '#E0E0E0' },
    '&:hover fieldset': { borderColor: '#83D84D' },
    '&.Mui-focused fieldset': { borderColor: '#83D84D' },
  },
};

const labelSx = { fontSize: '13px', color: '#737373', mb: '6px', fontWeight: 500 };

const STATUS_OPTIONS = ['Pago', 'Confirmado', 'Cancelado', 'Expirado', 'Pendente', 'Aguardando'];
const TYPE_OPTIONS = ['Compra', 'Consumo', 'Estorno'];
const PAYMENT_OPTIONS = ['Pix', 'Cartão', 'Boleto'];

const EMPTY_FILTERS = {
  companyId: '',
  status: '',
  type: '',
  paymentType: '',
  startDate: '',
  endDate: '',
};

const FilterModal = ({ open, onClose, onApply, initialFilters = EMPTY_FILTERS, resales = [] }) => {
  const { t } = useTranslation();
  const [local, setLocal] = useState(initialFilters);

  useEffect(() => {
    if (open) setLocal(initialFilters);
  }, [open, initialFilters]);

  const set = (field) => (e) => setLocal((prev) => ({ ...prev, [field]: e.target.value }));

  const handleClear = () => setLocal(EMPTY_FILTERS);

  const handleApply = () => {
    onApply(local);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2 }}
    >
      <Box
        sx={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          width: '100%',
          maxWidth: 480,
          maxHeight: '90vh',
          overflowY: 'auto',
          outline: 'none',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            p: '24px 24px 0 24px',
          }}
        >
          <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#1D2530' }}>
            {t('redscoreSuperAdmin.filterModal.title')}
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: '#9E9E9E', mt: '-4px' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ p: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <FormControl fullWidth>
            <Typography sx={labelSx}>{t('redscoreSuperAdmin.filterModal.resaleLabel')}</Typography>
            <Select value={local.companyId} onChange={set('companyId')} sx={inputSx} displayEmpty>
              <MenuItem value="">{t('redscoreSuperAdmin.filterModal.resaleAll')}</MenuItem>
              {resales.map((r) => (
                <MenuItem key={r.company_id} value={r.company_id}>
                  {r.company?.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <Typography sx={labelSx}>{t('redscoreSuperAdmin.filterModal.statusLabel')}</Typography>
            <Select value={local.status} onChange={set('status')} sx={inputSx} displayEmpty>
              <MenuItem value="">{t('redscoreSuperAdmin.filterModal.statusAll')}</MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <Typography sx={labelSx}>{t('redscoreSuperAdmin.filterModal.typeLabel')}</Typography>
            <Select value={local.type} onChange={set('type')} sx={inputSx} displayEmpty>
              <MenuItem value="">{t('redscoreSuperAdmin.filterModal.typeAll')}</MenuItem>
              {TYPE_OPTIONS.map((tp) => (
                <MenuItem key={tp} value={tp}>{tp}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <Typography sx={labelSx}>{t('redscoreSuperAdmin.filterModal.paymentLabel')}</Typography>
            <Select value={local.paymentType} onChange={set('paymentType')} sx={inputSx} displayEmpty>
              <MenuItem value="">{t('redscoreSuperAdmin.filterModal.paymentAll')}</MenuItem>
              {PAYMENT_OPTIONS.map((p) => (
                <MenuItem key={p} value={p}>{p}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Typography sx={labelSx}>{t('redscoreSuperAdmin.filterModal.periodLabel')}</Typography>
            <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <TextField
                type="date"
                label={t('redscoreSuperAdmin.filterModal.periodFrom')}
                value={local.startDate}
                onChange={set('startDate')}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={inputSx}
              />
              <TextField
                type="date"
                label={t('redscoreSuperAdmin.filterModal.periodTo')}
                value={local.endDate}
                onChange={set('endDate')}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={inputSx}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', pt: '4px' }}>
            <Button
              fullWidth={false}
              onClick={handleClear}
              sx={{
                border: '1px solid #E0E0E0',
                color: '#1D2530',
                backgroundColor: '#fff',
                '&:hover': { backgroundColor: '#F5F5F5' },
                px: '24px',
              }}
            >
              {t('redscoreSuperAdmin.filterModal.clearFilters')}
            </Button>
            <Button
              fullWidth={false}
              onClick={handleApply}
              sx={{
                backgroundColor: '#83D84D',
                '&:hover': { backgroundColor: '#6bb03a' },
                color: '#1D2530',
                fontWeight: 700,
                px: '24px',
              }}
            >
              {t('redscoreSuperAdmin.filterModal.applyFilters')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default FilterModal;
